import { calculateJkt } from './dpopUtils';
import { DpopKeyState } from './types';

const ECDSA_PARAMS: EcKeyGenParams = { name: 'ECDSA', namedCurve: 'P-256' };

const generateExtractableDpopKeyPair = async (): Promise<{
  privateKey: CryptoKey;
  publicKey: CryptoKey;
}> => {
  const keyPair = await crypto.subtle.generateKey(ECDSA_PARAMS, true, ['sign', 'verify']);

  return { privateKey: keyPair.privateKey, publicKey: keyPair.publicKey };
};

const exportPrivateJwk = async (privateKey: CryptoKey): Promise<JsonWebKey> =>
  crypto.subtle.exportKey('jwk', privateKey);

const exportPublicJwk = async (publicKey: CryptoKey): Promise<JsonWebKey> => {
  const full = await crypto.subtle.exportKey('jwk', publicKey);

  return {
    kty: full.kty,
    crv: full.crv,
    x: full.x,
    y: full.y,
  };
};

const importPrivateJwk = async (privateJwk: JsonWebKey): Promise<CryptoKey> =>
  crypto.subtle.importKey('jwk', privateJwk, ECDSA_PARAMS, false, ['sign']);

const publicJwkFromPrivateJwk = (privateJwk: JsonWebKey): JsonWebKey => ({
  kty: privateJwk.kty,
  crv: privateJwk.crv,
  x: privateJwk.x,
  y: privateJwk.y,
});

/**
 * In-memory singleton holding the current DPoP key pair.
 *
 * Lifetime: scoped to browser tab — lost on reload, restored via unlockPin.
 *
 * Normal flow:
 *   1. prepareForLogin() — called automatically by dpopFetch before /auth/login.
 *      Generates the key, loads it into state (ready to sign proofs), and keeps
 *      the raw JWK in pendingPrivateJwk for setupPin to encrypt.
 *   2. setupPin() — calls consumePendingJwk() to get the JWK, encrypts it, then
 *      stores the ciphertext on the server. pendingPrivateJwk is cleared.
 *   3. After page reload: unlockPin() → restore() reloads the key from the
 *      decrypted payload back into state.
 */
class DpopKeyManager {
  private state: DpopKeyState | null = null;
  private pendingPrivateJwk: JsonWebKey | null = null;
  // Serializes concurrent prepareForLogin() calls — only one key generation runs at a time.
  private preparePromise: Promise<void> | null = null;

  private async generateKeyPair(): Promise<{
    privateJwk: JsonWebKey;
    publicJwk: JsonWebKey;
    jkt: string;
    privateKey: CryptoKey;
  }> {
    const { privateKey: extractablePrivateKey, publicKey } = await generateExtractableDpopKeyPair();

    const [privateJwk, publicJwk] = await Promise.all([
      exportPrivateJwk(extractablePrivateKey),
      exportPublicJwk(publicKey),
    ]);

    const [jkt, privateKey] = await Promise.all([
      calculateJkt(publicJwk),
      importPrivateJwk(privateJwk),
    ]);

    return { privateJwk, publicJwk, jkt, privateKey };
  }

  /**
   * Generate a key pair and keep the JWK in pendingPrivateJwk so that dpopFetch
   * can sign a proof on /auth/login, and setupPin can later encrypt the JWK.
   * No-op if a key is already loaded (e.g. after unlockPin).
   * Safe to call concurrently — only one generation runs at a time.
   */
  async prepareForLogin(): Promise<void> {
    if (this.state) return Promise.resolve();

    if (this.preparePromise) return this.preparePromise;
    this.preparePromise = (async () => {
      if (this.state) return;

      const { privateJwk, publicJwk, jkt, privateKey } = await this.generateKeyPair();
      this.state = { privateKey, publicJwk, privateJwk, jkt };

      this.pendingPrivateJwk = privateJwk;
    })().finally(() => {
      this.preparePromise = null;
    });

    return this.preparePromise;
  }

  /**
   * Consume and return the pending JWK (called once by setupPin to encrypt it).
   * Returns null if there is no pending JWK (key was restored via unlockPin).
   */
  consumePendingJwk(): JsonWebKey | null {
    const jwk = this.pendingPrivateJwk;
    this.pendingPrivateJwk = null;

    return jwk;
  }

  async exportCurrentJwk(): Promise<JsonWebKey | null> {
    return this.state?.privateJwk ?? null;
  }

  async generate(): Promise<{ privateJwk: JsonWebKey }> {
    const { privateJwk, publicJwk, jkt, privateKey } = await this.generateKeyPair();
    this.state = { privateKey, publicJwk, privateJwk, jkt };

    return { privateJwk };
  }

  /**
   * Restore the key from a decrypted private JWK (after unlock).
   * Returns the computed jkt so the caller can verify it matches the session.
   */
  async restore(privateJwk: JsonWebKey): Promise<{ jkt: string }> {
    const publicJwk = publicJwkFromPrivateJwk(privateJwk);

    const [privateKey, jkt] = await Promise.all([
      importPrivateJwk(privateJwk),
      calculateJkt(publicJwk),
    ]);
    this.state = { privateKey, publicJwk, privateJwk, jkt };

    return { jkt };
  }

  get(): DpopKeyState | null {
    return this.state;
  }

  hasKey(): boolean {
    return this.state !== null;
  }

  clear(): void {
    this.state = null;
    this.pendingPrivateJwk = null;
  }
}

export const dpopKeyManager = new DpopKeyManager();
